/**
 * Layout for the printable weekly planner: a time grid with a column per day, or a box
 * per day with ruled lines. Positions are millimetres on a page from `pageGeometry`.
 *
 * The layout works on strings the caller has already put into the reader's language
 * (day names, dates, hour labels), so it is locale-free and easy to test.
 */

import {
  addDays,
  type CivilDate,
  shortDate,
  type WeekStart,
  weekday,
  weekdayNames,
} from "@/lib/tool-kit/printables/calendar";
import { type PageGeometry, r2 } from "@/lib/tool-kit/printables/page";
import type { PlacedText } from "@/lib/tool-kit/printables/reward-chart";
import {
  type FontSpec,
  fitSize,
  type TextMeasure,
} from "@/lib/tool-kit/printables/text";

const BOLD: FontSpec = { kind: "sans", bold: true };

export type PlannerLayout = "grid" | "lists";

export interface PlannerLine {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  /** Stroke weight: heavy outline, normal rule or faint guide. */
  weight: "heavy" | "normal" | "faint";
  dashed?: boolean;
}

export interface PlannerBox {
  x: number;
  y: number;
  width: number;
  height: number;
  fill?: string;
}

export interface PlannerPage {
  texts: PlacedText[];
  lines: PlannerLine[];
  boxes: PlannerBox[];
  /** Small tick boxes (priorities and to-do lines). */
  checks: PlannerBox[];
}

export interface PlannerOptions {
  layout: PlannerLayout;
  title: string;
  weekLabel: string;
  /** The date range typed on the page, or empty for a blank line to write on. */
  rangeLabel: string;
  dayNames: readonly string[];
  /** One date per day (empty strings when the planner is undated). */
  dateLabels: readonly string[];
  showBand: boolean;
  priorityLabel: string;
  notesLabel: string;
  /** For the grid layout: a label per hour from the first to the last. */
  hourLabels: readonly string[];
  halfHours: boolean;
}

const BAND_MM = 24;
const HEADER_MM = 16;
const DAY_HEAD_MM = 9;
const TIME_COL_MM = 13;
const LINE_STEP_MM = 7.5;
const CHECK_MM = 3.4;

function text(
  value: string,
  x: number,
  y: number,
  size: number,
  anchor: PlacedText["anchor"] = "start",
  bold = false
): PlacedText {
  return { text: value, x: r2(x), y: r2(y), size: r2(size), anchor, bold };
}

function line(
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  weight: PlannerLine["weight"] = "normal",
  dashed = false
): PlannerLine {
  return { x1: r2(x1), y1: r2(y1), x2: r2(x2), y2: r2(y2), weight, dashed };
}

function box(x: number, y: number, width: number, height: number): PlannerBox {
  return { x: r2(x), y: r2(y), width: r2(width), height: r2(height) };
}

/** Title, "Week of" and its writing line. Returns the y where the body can start. */
function header(
  page: PlannerPage,
  geometry: PageGeometry,
  measure: TextMeasure,
  options: PlannerOptions
): number {
  const left = geometry.left;
  const right = geometry.left + geometry.contentWidth;
  const size = fitSize(
    measure,
    options.title,
    BOLD,
    geometry.contentWidth * 0.5,
    9,
    4
  );
  page.texts.push(
    text(options.title, left, geometry.top + 8, size, "start", true)
  );
  const labelWidth = measure(options.weekLabel, { kind: "sans" }) * 4.4;
  const lineStart = right - geometry.contentWidth * 0.34;
  if (options.rangeLabel) {
    page.texts.push(
      text(options.rangeLabel, right, geometry.top + 7.5, 5, "end", true)
    );
  } else {
    page.texts.push(
      text(
        options.weekLabel,
        lineStart - labelWidth - 1.5,
        geometry.top + 7.5,
        4.4
      )
    );
    page.lines.push(line(lineStart, geometry.top + 8, right, geometry.top + 8));
  }
  return geometry.top + HEADER_MM;
}

/** Priorities (with tick boxes) and notes, side by side. Returns the band's bottom. */
function band(
  page: PlannerPage,
  geometry: PageGeometry,
  options: PlannerOptions,
  top: number
): number {
  const left = geometry.left;
  const half = geometry.contentWidth / 2 - 2;
  page.boxes.push(box(left, top, half, BAND_MM));
  page.boxes.push(box(left + half + 4, top, half, BAND_MM));
  page.texts.push(
    text(options.priorityLabel, left + 2, top + 4.6, 3.6, "start", true),
    text(options.notesLabel, left + half + 6, top + 4.6, 3.6, "start", true)
  );
  for (let row = 0; row < 3; row += 1) {
    const y = top + 10.5 + row * 5.6;
    page.checks.push(box(left + 2, y - CHECK_MM + 0.6, CHECK_MM, CHECK_MM));
    page.lines.push(line(left + 7, y + 0.6, left + half - 2, y + 0.6, "faint"));
  }
  return top + BAND_MM + 4;
}

function gridBody(
  page: PlannerPage,
  geometry: PageGeometry,
  options: PlannerOptions,
  top: number
): void {
  const days = options.dayNames.length;
  const left = geometry.left;
  const bottom = geometry.top + geometry.contentHeight;
  const gridLeft = left + TIME_COL_MM;
  const dayWidth = (geometry.contentWidth - TIME_COL_MM) / days;
  const hours = Math.max(1, options.hourLabels.length);
  const rowHeight = (bottom - top - DAY_HEAD_MM) / hours;
  const bodyTop = top + DAY_HEAD_MM;
  page.boxes.push(box(left, top, geometry.contentWidth, bottom - top));
  page.lines.push(line(left, bodyTop, left + geometry.contentWidth, bodyTop));
  for (let day = 0; day < days; day += 1) {
    const x = gridLeft + dayWidth * day;
    page.lines.push(line(x, top, x, bottom));
    page.texts.push(
      text(
        options.dayNames[day] ?? "",
        x + dayWidth / 2,
        top + 4.2,
        3.8,
        "middle",
        true
      )
    );
    if (options.dateLabels[day]) {
      page.texts.push(
        text(
          options.dateLabels[day] ?? "",
          x + dayWidth / 2,
          top + 7.7,
          3,
          "middle"
        )
      );
    }
  }
  for (let hour = 0; hour < hours; hour += 1) {
    const y = bodyTop + rowHeight * hour;
    if (hour > 0) {
      page.lines.push(line(left, y, left + geometry.contentWidth, y, "normal"));
    }
    page.texts.push(
      text(
        options.hourLabels[hour] ?? "",
        left + TIME_COL_MM - 1.5,
        y + 3.2,
        2.9,
        "end"
      )
    );
    if (options.halfHours) {
      page.lines.push(
        line(
          gridLeft,
          y + rowHeight / 2,
          left + geometry.contentWidth,
          y + rowHeight / 2,
          "faint",
          true
        )
      );
    }
  }
}

function listsBody(
  page: PlannerPage,
  geometry: PageGeometry,
  options: PlannerOptions,
  top: number
): void {
  const cells = options.dayNames.length + 1;
  const landscape = geometry.orientation === "landscape";
  const columns = landscape ? Math.ceil(cells / 2) : 2;
  const rows = Math.ceil(cells / columns);
  const bottom = geometry.top + geometry.contentHeight;
  const width = geometry.contentWidth / columns;
  const height = (bottom - top) / rows;
  for (let index = 0; index < cells; index += 1) {
    const column = index % columns;
    const row = Math.floor(index / columns);
    const x = geometry.left + width * column;
    const y = top + height * row;
    page.boxes.push(box(x, y, width, height));
    const isNotes = index === cells - 1;
    const heading = isNotes
      ? options.notesLabel
      : (options.dayNames[index] ?? "");
    page.texts.push(text(heading, x + 2.5, y + 5.2, 4, "start", true));
    const date = isNotes ? "" : (options.dateLabels[index] ?? "");
    if (date) {
      page.texts.push(text(date, x + width - 2.5, y + 5.2, 3.2, "end"));
    }
    page.lines.push(line(x, y + 8, x + width, y + 8));
    for (
      let ruled = y + 8 + LINE_STEP_MM;
      ruled < y + height - 1;
      ruled += LINE_STEP_MM
    ) {
      page.lines.push(line(x + 2, ruled, x + width - 2, ruled, "faint"));
    }
  }
}

/** One page of the planner. */
export function layoutPlannerPage(
  geometry: PageGeometry,
  measure: TextMeasure,
  options: PlannerOptions
): PlannerPage {
  const page: PlannerPage = { texts: [], lines: [], boxes: [], checks: [] };
  let top = header(page, geometry, measure, options);
  if (options.showBand) {
    top = band(page, geometry, options, top);
  }
  if (options.layout === "grid") {
    gridBody(page, geometry, options, top);
  } else {
    listsBody(page, geometry, options, top);
  }
  return page;
}

/* ------------------------------------------------------------------- the days */

/** The first day of the week that contains `date`. */
export function weekStartOf(date: CivilDate, weekStart: WeekStart): CivilDate {
  const back = (weekday(date.year, date.month, date.day) - weekStart + 7) % 7;
  return addDays(date, -back);
}

export interface WeekDays {
  dayNames: string[];
  /** Empty strings when no start date was given. */
  dateLabels: string[];
  /** First and last date shown, for the page heading; null when undated. */
  first: CivilDate | null;
  last: CivilDate | null;
}

/**
 * The columns of one week: seven days from the week start, or Monday to Friday.
 * `start` is the first day of that week, or null for an undated planner.
 */
export function weekDays(
  locale: string,
  weekStart: WeekStart,
  weekdaysOnly: boolean,
  start: CivilDate | null
): WeekDays {
  const names = weekdayNames(locale, weekStart, "long");
  const dayNames: string[] = [];
  const dateLabels: string[] = [];
  let first: CivilDate | null = null;
  let last: CivilDate | null = null;
  for (let index = 0; index < 7; index += 1) {
    const weekdayNumber = (weekStart + index) % 7;
    if (weekdaysOnly && (weekdayNumber === 0 || weekdayNumber === 6)) {
      continue;
    }
    dayNames.push(names[index] ?? "");
    if (start) {
      const date = addDays(start, index);
      dateLabels.push(shortDate(locale, date));
      first = first ?? date;
      last = date;
    } else {
      dateLabels.push("");
    }
  }
  return { dayNames, dateLabels, first, last };
}
